<template>
  <article class="stageCard" :class="{ 'stageCard--first': isFirst }">
    <div class="stageDateBadge">
      <span class="stageDateBadgeText">{{ dayLabel }}</span>
    </div>

    <div class="stageBody">
      <div class="stageHeading">
        <!-- Název scény -->
        <div class="stageName">{{ stage.name | translation }}</div>

        <!-- Adresa scény -->
        <div
          v-if="$options.filters.translation(stage.note)"
          class="stageAddress"
        >
          ({{ stage.note | translation }})
        </div>
      </div>

      <div class="events">
        <div
          v-for="(group, gIdx) in eventGroups"
          :key="gIdx"
          class="eventGroup"
        >
          <ProgramEventItem
            v-for="(ev, eIdx) in group.events"
            :key="ev.id || gIdx + '-' + eIdx"
            :event="ev"
            :showMeta="
              hasAnyTicket &&
                eIdx === group.events.length - 1 &&
                !!group.ticketLink
            "
            :metaGenres="group.genres"
            :metaTicketLink="group.ticketLink"
          />
        </div>
      </div>

      <!-- Stage bez ticketů: žánry na konci -->
      <div v-if="!hasAnyTicket && stageGenres.length" class="stageMeta">
        <div class="genres">
          <span
            v-for="g in stageGenres"
            :key="g.id || (g.name && g.name.cs) || JSON.stringify(g)"
            class="genre"
          >
            <span
              class="genreDot"
              :style="{ backgroundColor: genreColor(g) }"
            ></span>
            <span class="genreName">{{ g.name | translation }}</span>
          </span>
        </div>
      </div>
    </div>
  </article>
</template>

<script>
import ProgramEventItem from "@/components/base/program/mainProgram/ProgramEventItem.vue";

export default {
  name: "ProgramStageCard",
  components: { ProgramEventItem },
  props: {
    stage: { type: Object, required: true },
    dayLabel: { type: String, required: true },
    isFirst: { type: Boolean, default: false },
  },
  computed: {
    sortedEvents() {
      const events = Array.isArray(this.stage?.events) ? this.stage.events : [];
      const toMs = (t) => {
        if (!t) return Number.MAX_SAFE_INTEGER;
        const d = new Date(String(t).replace(" ", "T"));
        return isNaN(d.getTime()) ? Number.MAX_SAFE_INTEGER : d.getTime();
      };
      return [...events].sort((a, b) => toMs(a.timeFrom) - toMs(b.timeFrom));
    },
    hasAnyTicket() {
      return this.sortedEvents.some((e) => !!e.ticketLink);
    },
    eventGroups() {
      if (!this.hasAnyTicket)
        return [{ ticketLink: null, events: this.sortedEvents, genres: [] }];

      const groups = [];
      let current = null;

      const flush = () => {
        if (!current) return;
        current.genres = this.uniqueGenresFromEvents(current.events);
        groups.push(current);
        current = null;
      };

      for (const ev of this.sortedEvents) {
        const link = ev?.ticketLink || null;

        if (!link) {
          flush();
          groups.push({ ticketLink: null, events: [ev], genres: [] });
          continue;
        }

        if (!current) current = { ticketLink: link, events: [ev], genres: [] };
        else if (current.ticketLink === link) current.events.push(ev);
        else {
          flush();
          current = { ticketLink: link, events: [ev], genres: [] };
        }
      }

      flush();
      return groups;
    },
    stageGenres() {
      if (this.hasAnyTicket) return [];
      return this.uniqueGenresFromEvents(this.sortedEvents);
    },
  },
  methods: {
    genreColor(g) {
      const c = g && g.color;
      return typeof c === "string" && c.trim().length > 0 ? c : "#000";
    },
    uniqueGenresFromEvents(events) {
      const all = [];
      (events || []).forEach((ev) => {
        (ev?.interprets || []).forEach((i) => {
          (i?.genres || []).forEach((g) => all.push(g));
        });
      });

      const out = [];
      const seen = new Set();

      all.forEach((g) => {
        const key =
          g?.id ?? g?.name?.cs ?? g?.name?.en ?? JSON.stringify(g?.name || g);
        if (!seen.has(key)) {
          seen.add(key);
          out.push(g);
        }
      });

      return out;
    },
  },
};
</script>

<style scoped>
.stageCard {
  position: relative;
  background: #ffce6d;

  border-left: 2px solid #000;
  border-right: 2px solid #000;
  border-bottom: 2px solid #000;
  border-top: 0;

  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.22);
  margin: 0;
}

.stageCard--first {
  border-top: 0;
}

/* Badge – DESKTOP 17 */
.stageDateBadge {
  position: absolute;
  top: -1px;
  right: -1px;
  text-transform: lowercase;

  background: #1f1f1f;
  color: #ffce6d;

  padding: 6px 16px; /* ↓ menší vertikální padding */
}

.stageDateBadgeText {
  font-family: "Transcript Pro Mono Regular";
  font-weight: 700;
  font-size: 17px;

  line-height: 1; /* pevnější výška */
  white-space: nowrap;
}

.stageBody {
  padding: 22px 24px 24px 24px;
}
.stageHeading {
  margin-bottom: 12px;
}

/* Název scény – DESKTOP 24 */
.stageName {
  font-family: "Transcript Pro Mono Regular";
  font-weight: 700;
  font-size: 24px;
  line-height: 1.15;
  color: #000;
}

/* Adresa scény – DESKTOP 17 */
.stageAddress {
  margin-top: 6px;
  font-family: "Transcript Pro Mono Regular";
  font-weight: 700;
  font-size: 17px;
  line-height: 1.2;
  color: #000;
}

.events {
  margin-top: 12px;
}
.eventGroup + .eventGroup {
  margin-top: 16px;
}

/* Stage žánry – DESKTOP 17 */
.stageMeta {
  margin-top: 16px;
}
.genres {
  display: block;
}

.genre {
  display: inline-flex;
  align-items: center;
  margin-right: 12px;
  margin-bottom: 8px;

  font-family: "Transcript Pro Mono Regular";
  font-weight: 700;
  font-size: 17px;
  line-height: 1.1;
  color: #000;
}

.genreDot {
  display: inline-block;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  border: none;
  margin-right: 8px;
  position: relative;
  top: -2.5px;
}

/* MOBILE poměry:
   badge 16, stageName 20, stageAddress 14, genre 14 */
@media (max-width: 768px) {
  .genreDot {
    width: 10px;
    height: 10px;
  }

  .stageCard {
    overflow: hidden;
  }

  /* rezerva pro badge */
  .stageBody {
    padding: 18px 60px 20px 18px;
  }

  .stageDateBadge {
    top: 0 !important;
    right: 0 !important;
    transform: none !important;
    padding: 10px 8px;
  }

  .stageDateBadgeText {
    font-size: 16px;
    width: 16px;
    writing-mode: vertical-rl;
    transform: rotate(180deg);
    white-space: nowrap;
    line-height: 1;
  }

  .stageName {
    font-size: 20px;
    line-height: 1.15;
  }

  .stageAddress {
    font-size: 14px;
    line-height: 1.2;
  }

  .genre {
    font-size: 14px;
    line-height: 1.1;
  }
}
</style>
